import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import sliderService, { Slider } from '../../../services/slider';

const SliderManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<Slider[]>([]);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      navigate('/dashboard', { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await sliderService.getAll();
      setList(res);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('آیا از حذف این اسلایدر اطمینان دارید؟')) return;
    try {
      await sliderService.delete(id);
      load();
    } catch (error) {
      console.error('Error deleting slider:', error);
      alert('خطا در حذف اسلایدر');
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">مدیریت اسلایدرها</h1>
          <p className="text-gray-500 mt-1">مدیریت تصاویر و محتوای اسلایدر صفحه اصلی</p>
        </div>
        <button
          onClick={() => navigate('/admin/sliders/new')}
          className="px-6 py-2.5 rounded-xl bg-[#0e444e] text-white hover:bg-[#0b363e] shadow-lg shadow-[#0e444e]/20 font-medium transition-all"
        >
          افزودن اسلایدر جدید
        </button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">تصویر</th>
                <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">عنوان</th>
                <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">ترتیب</th>
                <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">وضعیت</th>
                <th className="text-center py-4 px-6 text-sm font-semibold text-gray-600 w-32">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    هیچ اسلایدری یافت نشد
                  </td>
                </tr>
              ) : (
                list.map((slider) => (
                  <tr key={slider.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="w-24 h-12 rounded-lg overflow-hidden bg-gray-100">
                        <img 
                          src={slider.image_url} 
                          alt={slider.title} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="font-medium text-gray-900">{slider.title}</div>
                      {slider.subtitle && (
                        <div className="text-xs text-gray-500 mt-0.5">{slider.subtitle}</div>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600 font-medium text-sm">
                        {slider.display_order}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          slider.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {slider.is_active ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => navigate(`/admin/sliders/${slider.id}/edit`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="ویرایش"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(slider.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="حذف"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SliderManagementPage;
